package link.erpango.quip.data.db.entities

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.async.coroutines.awaitAsOneOrNull
import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.async.coroutines.awaitAsOne
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import link.erpango.quip.db.QuipDb
import link.erpango.quip.domain.entities.DVehicle
import linkerpangoquipdb.Vehicle
import link.erpango.quip.domain.entities.VehicleType
import kotlin.coroutines.CoroutineContext

class VehiclesDAO(
    val db: QuipDb
): IGarageTable {
    override suspend fun insertVehicle(
        vin: String,
        plate: String,
        brand: String,
        model: String,
        vehicleType: String,
        year: Long,
        color: String,
        owner: String,
        engineOil: String,
        frontTireSize: String,
        rearTireSize: String,
        frontTirePressureBelow160: Double,
        rearTirePressureBelow160: Double,
        frontTirePressureAbove160: Double,
        rearTirePressureAbove160: Double,
        recommendedFuel: String,
        engineDetails: String,
        cylinderCount: Int,
        frontBulbType: String,
        rearBulbType: String,
        batterySpecs: String,
        coolantType: String,
        signature: String?,
        nextRevisionDate: Long?,
        nextItvDate: Long?,
        firstRegistrationDate: Long?
    ) {
        db.garageQueries.insertVehicle(
            vin = vin,
            plate = plate,
            brand = brand,
            model = model,
            vehicle_type = vehicleType,
            year = year,
            color = color,
            owner = owner,
            engine_oil = engineOil,
            front_tire_size = frontTireSize,
            rear_tire_size = rearTireSize,
            front_tire_pressure_below_160 = frontTirePressureBelow160,
            rear_tire_pressure_below_160 = rearTirePressureBelow160,
            front_tire_pressure_above_160 = frontTirePressureAbove160,
            rear_tire_pressure_above_160 = rearTirePressureAbove160,
            recommended_fuel = recommendedFuel,
            engine_details = engineDetails,
            cylinder_count = cylinderCount,
            front_bulb_type = frontBulbType,
            rear_bulb_type = rearBulbType,
            battery_specs = batterySpecs,
            coolant_type = coolantType,
            signature = signature,
            next_revision_date = nextRevisionDate,
            next_itv_date = nextItvDate,
            first_registration_date = firstRegistrationDate
        )
    }

    override suspend fun selectVehicle(vin: String): DVehicle? {
        return db.garageQueries.select(vin).awaitAsOneOrNull()?.toDVehicle()
    }

    override suspend fun selectAllVehicles(): List<DVehicle> {
        return db.garageQueries.select_all().awaitAsList().map {
            it.toDVehicle()
        }
    }

    override fun observeVehicles(coroutineContext: CoroutineContext): Flow<List<DVehicle>> {
        return db.garageQueries.select_all()
            .asFlow()
            .mapToList(coroutineContext)
            .map { list ->
                val resultList = ArrayList<DVehicle>(list.size)
                for (vehicleRow in list) {
                     val vehicleMaintenances =
                        db.maintenanceQueries
                            .select_by_vehicle_vin(vehicleRow.vin)
                            .awaitAsList()
                            .map { maintenance ->
                                val maintenanceSparePartsList =
                                    db.maintenanceSparePartsQueries
                                        .select_by_maintenance_id(maintenance.id)
                                        .awaitAsList()
                                        .map {
                                            db.sparePartQueries
                                                .select_by_id(it.spare_part_id)
                                                .awaitAsOne()
                                                .toDSparePart()
                                        }
                                maintenance.toDMaintenance().apply {
                                    spareParts = maintenanceSparePartsList
                                }
                            }
                     val dVehicle = vehicleRow.toDVehicle().apply {
                        maintenances = vehicleMaintenances
                     }
                     resultList.add(dVehicle)
                }
                resultList
            }
    }

    override suspend fun updateVehicle(
        vin: String,
        plate: String,
        brand: String,
        model: String,
        vehicleType: String,
        year: Long,
        color: String,
        owner: String,
        engineOil: String,
        frontTireSize: String,
        rearTireSize: String,
        frontTirePressureBelow160: Double,
        rearTirePressureBelow160: Double,
        frontTirePressureAbove160: Double,
        rearTirePressureAbove160: Double,
        recommendedFuel: String,
        engineDetails: String,
        cylinderCount: Int,
        frontBulbType: String,
        rearBulbType: String,
        batterySpecs: String,
        coolantType: String,
        signature: String?,
        nextRevisionDate: Long?,
        nextItvDate: Long?,
        firstRegistrationDate: Long?
    ) {
        db.garageQueries.update(
            vin = vin,
            plate = plate,
            brand = brand,
            model = model,
            vehicle_type = vehicleType,
            year = year,
            color = color,
            owner = owner,
            engine_oil = engineOil,
            front_tire_size = frontTireSize,
            rear_tire_size = rearTireSize,
            front_tire_pressure_below_160 = frontTirePressureBelow160,
            rear_tire_pressure_below_160 = rearTirePressureBelow160,
            front_tire_pressure_above_160 = frontTirePressureAbove160,
            rear_tire_pressure_above_160 = rearTirePressureAbove160,
            recommended_fuel = recommendedFuel,
            engine_details = engineDetails,
            cylinder_count = cylinderCount,
            front_bulb_type = frontBulbType,
            rear_bulb_type = rearBulbType,
            battery_specs = batterySpecs,
            coolant_type = coolantType,
            signature = signature,
            next_revision_date = nextRevisionDate,
            next_itv_date = nextItvDate,
            first_registration_date = firstRegistrationDate
        )
    }
    
    override suspend fun selectDistinctOwners(): List<String> {
        return db.garageQueries.select_distinct_owners().awaitAsList()
    }

    override suspend fun deleteVehicle(vin: String) {
        db.garageQueries.delete(vin)
    }
}

fun Vehicle.toDVehicle(): DVehicle {
    return DVehicle(
        vin = vin,
        plate = plate,
        brand = brand,
        model = model,
        vehicleType = VehicleType.fromCode(vehicle_type),
        year = year.toInt(),
        color = color,
        owner = owner,
        engineOil = engine_oil,
        frontTireSize = front_tire_size,
        rearTireSize = rear_tire_size,
        frontTirePressureBelow160 = front_tire_pressure_below_160,
        rearTirePressureBelow160 = rear_tire_pressure_below_160,
        frontTirePressureAbove160 = front_tire_pressure_above_160,
        rearTirePressureAbove160 = rear_tire_pressure_above_160,
        recommendedFuel = recommended_fuel,
        engineDetails = engine_details,
        cylinderCount = cylinder_count.toInt(),
        frontBulbType = front_bulb_type,
        rearBulbType = rear_bulb_type,
        batterySpecs = battery_specs,
        coolantType = coolant_type,
        signature = signature,
        nextRevisionDate = next_revision_date,
        nextItvDate = next_itv_date,
        firstRegistrationDate = first_registration_date,
        maintenances = emptyList()
    )
}